import styled from 'styled-components';
import {
  mobileBreakpoint,
  useIsMobile,
} from '@keyslabor/shared/hooks/useBreakPoint';
import { useLocation, Link as RouteLink, useNavigate } from 'react-router-dom';

import { color, ColorProps, typography, TypographyProps } from 'styled-system';
import { px } from '@/utils';
import { UnsavedScheduleTracking } from '@/components/PageLayout/PageLayout.tsx';
import { Box, Text } from '@keyslabor/shared/ui';
import { Routes } from '@/globalTypes';
import useEmployeeLeads from '@/hooks/api/useEmployeeLeads.ts';
import { leadsFilter } from '@/containers/Leads/types.ts';
import { Badge as AntBadge } from 'antd';

type LinkProps = {
  to?: string;
  label?: string;
  color?: string;
  isActive?: boolean;
  // TODO: Remove any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick?: (args: any) => void;
  unsavedScheduleTracking?: UnsavedScheduleTracking;
  fontWeight?: string;
};

// Note: Maybe its better to move unsavedScheduleTracking props in the Context and avoid prop drilling from schedule
// but it needed to consider cause of all the additional rerendering, and complexity it will add to app
export default function Link({
  label,
  to,
  onClick,
  isActive = false,
  unsavedScheduleTracking,
  fontWeight,
  ...rest
}: LinkProps & TypographyProps & ColorProps) {
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigation = useNavigate();
  const handleSavePrevRoute = () => {
    localStorage.setItem('prev-route', location.pathname);
  };

  const { isChanged, onSave } = unsavedScheduleTracking || {};

  const handleColor = (to: string) => {
    if (to === Routes.EmployeeList) {
      return '#569DE5';
    }

    if (to === Routes.InvoiceList) {
      return '#FE4C4C';
    }

    if (to === Routes.ScheduleList) {
      return '#219653';
    }

    return '#111111';
  };

  // @ts-expect-error TODO: fix types here
  const color = handleColor(to);

  const newPageBadge = (
    <Text
      color="#569DE5"
      fontSize={isMobile ? '10px' : '7px'}
      css={`
        text-transform: capitalize;
      `}
    >
      New
    </Text>
  );

  return (
    <Container
      onClick={() => {
        if (isChanged) {
          onSave?.({
            routeChange: true,
            // @ts-expect-error TODO: fix types here
            navigationCallback: () => navigation(to),
          });
        }
      }}
    >
      <StyledLinkContainer
        isActive={isActive}
        isMobile={isMobile}
        color={color}
      >
        <StyledLink
          to={to}
          css={`
            pointer-events: ${isChanged ? 'none' : 'initial'};
          `}
          onClick={(e) => {
            handleSavePrevRoute();
            onClick?.(e);
          }}
          title={label}
          fontSize={14}
          isActive={isActive}
          color={color}
          fontWeight={fontWeight ? fontWeight : ''}
          {...rest}
        >
          {label}
        </StyledLink>
        <Box
          position="absolute"
          top={isMobile ? '20px' : '-3px'}
          right={isMobile ? '-22px' : '-15px'}
        >
          {to === Routes.RequestEmployeeList || to === Routes.TurnOver
            ? newPageBadge
            : null}

          {to === Routes.LeadsInhouse ? <CandidatesBadge /> : null}
        </Box>
      </StyledLinkContainer>
    </Container>
  );
}

const Container = styled.div`
  display: inline-block !important;
  font-size: 0;
  font-family: 'Roboto';
  text-transform: uppercase;
  position: relative;
  margin-right: 20px;
  transition: 0.3s;
`;

const StyledLink = styled(RouteLink)<
  ColorProps & TypographyProps & { isActive: boolean; fontWeight: string }
>`
  ${color}
  ${typography}

  display: inline-block;
  text-decoration: none;
  font-weight: ${({ fontWeight, isActive }) =>
    fontWeight ? fontWeight : isActive ? '500' : '400'};
  color: ${color};

  @media screen and (max-width: ${px(mobileBreakpoint)}) {
    font-size: 20px;
    margin-top: 25px;
  }
`;

const StyledLinkContainer = styled.div<{
  isActive: boolean;
  isMobile: boolean;
}>`
  position: relative;
  padding-bottom: 2px;
  &:hover {
    font-weight: 500;
    &:after {
      width: 100%;
    }
  }

  &:before {
    display: block;
    content: attr(title);
    font-weight: 500;
    height: 0;
    overflow: hidden;
    visibility: hidden;
  }

  &:after {
    content: '';
    width: ${({ isActive, isMobile }) =>
      !isMobile && isActive ? '100%' : '0'};
    position: absolute;
    left: 0;
    top: 100%;
    height: 2px;
    background-color: ${({ color }) => color};
    transition: width 0.3s ease-in-out;
  }
`;

const CandidatesBadge = () => {
  const isMobile = useIsMobile();

  const { leads: inWorkInhouseLeads } = useEmployeeLeads(leadsFilter.inhouse, [
    'in_work',
  ]);
  const { leads: inWorkByAgencyLeads } = useEmployeeLeads(leadsFilter.agency, [
    'in_work',
  ]);

  const totalCandidates =
    +inWorkByAgencyLeads?.count + +inWorkInhouseLeads?.count || 0;

  if (totalCandidates > 0) {
    return (
      <AntBadge
        count={totalCandidates}
        css={`
          width: 16px;
          height: 16px;
          transform: translate(-4px, -4px);
          & > sup.ant-scroll-number.ant-badge-count {
            font-size: 8px;
            height: 100%;
            min-width: unset;
            border-radius: 50%;
            line-height: 16px;
            box-shadow: none;
          }
        `}
      />
    );
  }

  return (
    <Text
      color="#569DE5"
      fontSize={isMobile ? '10px' : '7px'}
      css={`
        text-transform: capitalize;
      `}
    >
      New
    </Text>
  );
};
